// Serializing Lexical editor state to and from plain text. Pure, and shared by the display, the editor
// and anything that compares two versions of a body.

export const IMAGE_PLACEHOLDER = '[image]';

/**
 * Walk serialized Lexical JSON to plain text, with `blockSeparator` between top-level blocks.
 */
export function lexicalSerializedToPlainText(parsed, { blockSeparator = '\n' } = {}) {
    const visit = (node) => {
        if (!node) return '';
        if (node.type === 'text') return node.text || '';
        if (node.type === 'mention') {
            const m = node.mention;
            return m?.fullName || m?.name || '';
        }
        if (node.type === 'image') return IMAGE_PLACEHOLDER;
        if (node.type === 'linebreak') return '\n';
        if (Array.isArray(node.children)) {
            return node.children.map(visit).join('');
        }
        return '';
    };
    const root = parsed?.root;
    if (!root?.children?.length) return '';
    return root.children.map((block) => visit(block)).join(blockSeparator);
}

/** The text a piece of content reads as, for comparing two versions of it: the same walk the display does, with the blank line a plain text sink puts between blocks. */
export function contentToPlainText(content) {
    let parsed = null;
    if (typeof content === 'string') {
        try {
            parsed = JSON.parse(content);
        } catch {
            parsed = null;
        }
    } else {
        parsed = content;
    }
    const text = parsed?.root?.children != null
        ? lexicalSerializedToPlainText(parsed, { blockSeparator: '\n\n' })
        : (typeof content === 'string' ? content : '');
    return text.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Build a Lexical paragraph node from plain text, every newline in it a line break. */
export function buildParagraphNode(text) {
    const children = [];
    String(text ?? '').split('\n').forEach((line, index) => {
        if (index > 0) children.push({ type: 'linebreak', version: 1 });
        if (line) children.push({ type: 'text', text: line, format: 0, mode: 'normal', style: '', detail: 0, version: 1 });
    });
    return {
        type: 'paragraph',
        children,
        direction: 'ltr',
        format: '',
        indent: 0,
        textFormat: 0,
        textStyle: '',
        version: 1,
    };
}

/** Minimal Lexical document: one paragraph with plain text (for compact table preview). */
export function buildLexicalSingleParagraphState(plainLine) {
    return JSON.stringify({
        root: {
            children: [buildParagraphNode(plainLine)],
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    });
}

/** Plain text to a Lexical editor state: a blank line starts a paragraph, any other newline is a line break — a paragraph per line adds a blank line on every round trip. */
export function buildLexicalFromPlainText(text) {
    const blocks = String(text).replace(/\r\n/g, '\n').split(/\n{2,}/);
    return JSON.stringify({
        root: {
            children: blocks.map((block) => buildParagraphNode(block)),
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    });
}

/** Check whether a string is a valid Lexical editor state (has root.children). */
export function isLexicalJson(str) {
    if (typeof str !== 'string') return false;
    try {
        const parsed = JSON.parse(str);
        return parsed?.root?.children != null;
    } catch {
        return false;
    }
}

// Root children must be element/decorator nodes — bare text/linebreak nodes at the root throw on parseEditorState.
export function rootChildrenAreValid(parsed) {
    const children = parsed?.root?.children;
    if (!Array.isArray(children)) return false;
    return children.every((c) => c && c.type !== 'text' && c.type !== 'linebreak');
}
